import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { Plus, Edit, Trash2, FileText } from 'lucide-react';
import type { Tables } from '@/integrations/supabase/types';

 type Prompt = Tables<'prompts'>;

 const promptSchema = z.object({
   name: z.string().min(1, 'Name is required'),
   purpose: z.string().optional(),
   prompt: z.string().min(1, 'Prompt is required'),
   system_prompt: z.string().optional(),
   model: z.string().optional(),
 });

 type PromptForm = z.infer<typeof promptSchema>;

 export default function PromptsPage() {
   const { toast } = useToast();
   const queryClient = useQueryClient();
   const [editingPrompt, setEditingPrompt] = useState<Prompt | null>(null);
   const [dialogOpen, setDialogOpen] = useState(false);

   const form = useForm<PromptForm>({
     resolver: zodResolver(promptSchema),
     defaultValues: {
       name: '',
       purpose: '',
       prompt: '',
       system_prompt: '',
       model: '',
     },
   });

   const { data: prompts = [], isLoading } = useQuery({
     queryKey: ['prompts'],
     queryFn: async () => {
       const { data, error } = await supabase
         .from('prompts')
         .select('*')
         .order('created_at', { ascending: false });
       if (error) throw error;
       return data || [];
     },
   });

   const createMutation = useMutation({
     mutationFn: async (data: PromptForm) => {
       const { error } = await supabase.from('prompts').insert(data);
       if (error) throw error;
     },
     onSuccess: () => {
       queryClient.invalidateQueries({ queryKey: ['prompts'] });
       toast({ title: 'Success', description: 'Prompt created successfully.' });
       setDialogOpen(false);
       form.reset();
     },
     onError: (error: any) => {
       toast({ title: 'Error', description: error.message, variant: 'destructive' });
     },
   });

   const updateMutation = useMutation({
     mutationFn: async ({ id, data }: { id: string; data: PromptForm }) => {
       const { error } = await supabase.from('prompts').update(data).eq('id', id);
       if (error) throw error;
     },
     onSuccess: () => {
       queryClient.invalidateQueries({ queryKey: ['prompts'] });
       toast({ title: 'Success', description: 'Prompt updated successfully.' });
       setDialogOpen(false);
       setEditingPrompt(null);
       form.reset();
     },
     onError: (error: any) => {
       toast({ title: 'Error', description: error.message, variant: 'destructive' });
     },
   });

   const deleteMutation = useMutation({
     mutationFn: async (id: string) => {
       const { error } = await supabase.from('prompts').delete().eq('id', id);
       if (error) throw error;
     },
     onSuccess: () => {
       queryClient.invalidateQueries({ queryKey: ['prompts'] });
       toast({ title: 'Success', description: 'Prompt deleted successfully.' });
     },
     onError: (error: any) => {
       toast({ title: 'Error', description: error.message, variant: 'destructive' });
     },
   });

   const onSubmit = (data: PromptForm) => {
     if (editingPrompt) {
       updateMutation.mutate({ id: editingPrompt.id, data });
     } else {
       createMutation.mutate(data);
     }
   };

   const handleEdit = (prompt: Prompt) => {
     setEditingPrompt(prompt);
     form.reset({
       name: prompt.name,
       purpose: prompt.purpose || '',
       prompt: prompt.prompt,
       system_prompt: prompt.system_prompt || '',
       model: prompt.model || '',
     });
     setDialogOpen(true);
   };

   const handleDelete = (id: string) => {
     if (confirm('Are you sure you want to delete this prompt?')) {
       deleteMutation.mutate(id);
     }
   };

   const openCreateDialog = () => {
     setEditingPrompt(null);
     form.reset({ name: '', purpose: '', prompt: '', system_prompt: '', model: '' });
     setDialogOpen(true);
   };

   if (isLoading) {
     return <div className="space-y-6">Loading prompts...</div>;
   }

   return (
     <div className="space-y-6">
       <div className="flex items-center justify-between">
         <div>
           <h1 className="text-3xl font-bold">Prompts</h1>
           <p className="text-muted-foreground">Manage reusable AI prompts</p>
         </div>
         <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
           <DialogTrigger asChild>
             <Button onClick={openCreateDialog} className="flex items-center gap-2">
               <Plus className="h-4 w-4" />
               Add Prompt
             </Button>
           </DialogTrigger>
           <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
             <DialogHeader>
               <DialogTitle>{editingPrompt ? 'Edit Prompt' : 'Create New Prompt'}</DialogTitle>
             </DialogHeader>
             <Form {...form}>
               <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                 <FormField
                   control={form.control}
                   name="name"
                   render={({ field }) => (
                     <FormItem>
                       <FormLabel>Name</FormLabel>
                       <FormControl>
                         <Input placeholder="Prompt name" {...field} />
                       </FormControl>
                       <FormMessage />
                     </FormItem>
                   )}
                 />
                 <FormField
                   control={form.control}
                   name="purpose"
                   render={({ field }) => (
                     <FormItem>
                       <FormLabel>Purpose</FormLabel>
                       <FormControl>
                         <Input placeholder="Purpose of this prompt" {...field} />
                       </FormControl>
                       <FormMessage />
                     </FormItem>
                   )}
                 />
                 <FormField
                   control={form.control}
                   name="model"
                   render={({ field }) => (
                     <FormItem>
                       <FormLabel>Model</FormLabel>
                       <FormControl>
                         <Input placeholder="Model e.g., gpt-4" {...field} />
                       </FormControl>
                       <FormMessage />
                     </FormItem>
                   )}
                 />
                 <FormField
                   control={form.control}
                   name="system_prompt"
                   render={({ field }) => (
                     <FormItem>
                       <FormLabel>System Prompt</FormLabel>
                       <FormControl>
                         <Textarea className="min-h-[60px]" {...field} />
                       </FormControl>
                       <FormMessage />
                     </FormItem>
                   )}
                 />
                 <FormField
                   control={form.control}
                   name="prompt"
                   render={({ field }) => (
                     <FormItem>
                       <FormLabel>Prompt</FormLabel>
                       <FormControl>
                         <Textarea className="min-h-[100px]" {...field} />
                       </FormControl>
                       <FormMessage />
                     </FormItem>
                   )}
                 />
                 <div className="flex justify-end gap-2">
                   <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                     Cancel
                   </Button>
                   <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                     {editingPrompt ? 'Update' : 'Create'} Prompt
                   </Button>
                 </div>
               </form>
             </Form>
           </DialogContent>
         </Dialog>
       </div>
       <Card>
         <CardHeader>
           <CardTitle>
             <FileText className="h-5 w-5 inline-block mr-2" />All Prompts
           </CardTitle>
         </CardHeader>
         <CardContent>
           {prompts.length === 0 ? (
             <div className="text-center py-8">
               <p className="text-muted-foreground">No prompts found.</p>
             </div>
           ) : (
             <Table>
               <TableHeader>
                 <TableRow>
                   <TableHead>Name</TableHead>
                   <TableHead>Purpose</TableHead>
                   <TableHead>Model</TableHead>
                   <TableHead>Actions</TableHead>
                 </TableRow>
               </TableHeader>
               <TableBody>
                 {prompts.map((p) => (
                   <TableRow key={p.id}>
                     <TableCell className="font-medium">{p.name}</TableCell>
                     <TableCell className="max-w-xs truncate">{p.purpose || '-'}</TableCell>
                     <TableCell>{p.model || '-'}</TableCell>
                     <TableCell>
                       <div className="flex gap-2">
                         <Button variant="outline" size="sm" onClick={() => handleEdit(p)}>
                           <Edit className="h-4 w-4" />
                         </Button>
                         <Button variant="outline" size="sm" onClick={() => handleDelete(p.id)} disabled={deleteMutation.isPending}>
                           <Trash2 className="h-4 w-4" />
                         </Button>
                       </div>
                     </TableCell>
                   </TableRow>
                 ))}
               </TableBody>
             </Table>
           )}
         </CardContent>
       </Card>
     </div>
   );
 }
